import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '@/components/common/toast';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { nopStressTestApi } from '@/api/services/nopStressTest';
import { formatNumber } from '@/utils/format';
import { StressTestTarget, StressTestType, type StressTestModifiedPriceDto, type PricesAndVolumesDto } from '@/api/types/nopStressTest';

// Live thousands-separator formatting for the New Price input (mirrors legacy
// formatWithCommas / formatPriceInputLive in stress-testing/scenario.js). Keeps a
// single decimal point, strips non-numeric, groups the integer part by 3.
function stripCommas(val: string): string {
  return val == null ? '' : String(val).replace(/,/g, '');
}
function formatWithCommas(val: string): string {
  if (val === '' || val == null) return '';
  const s = stripCommas(val);
  if (s === '' || s === '.') return s;
  const parts = s.split('.');
  const intDigits = parts[0].replace(/\D/g, '');
  const intPart = intDigits === '' ? '' : intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.length > 1 ? intPart + '.' + parts[1].replace(/\D/g, '') : intPart;
}
function cleanLive(raw: string): string {
  let cleaned = raw.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot !== -1) cleaned = cleaned.substring(0, firstDot + 1) + cleaned.substring(firstDot + 1).replace(/\./g, '');
  return formatWithCommas(cleaned);
}

const money = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, d));

export function ScenarioOffcanvas({
  open,
  savedPrices,
  stressType,
  target,
  onClose,
  onSave,
}: {
  open: boolean;
  savedPrices: StressTestModifiedPriceDto[];
  stressType: number;
  target: number;
  onClose: () => void;
  onSave: (prices: StressTestModifiedPriceDto[]) => void;
}) {
  const toast = useToast();
  const pv = useQuery({ queryKey: ['nst-pv'], queryFn: nopStressTestApi.getPricesAndVolumes, enabled: open });
  // New-price edits keyed by symbol (display string, comma-formatted). Persists
  // across open/close via the lazily-seeded ref below.
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);
  const seeded = useRef(false);

  // Seed from the previously-saved prices the first time the canvas opens (or
  // after a Clear resets it). Mirrors _restoreSavedPrices.
  useEffect(() => {
    if (!open) { seeded.current = false; return; }
    if (seeded.current) return;
    seeded.current = true;
    const seed: Record<string, string> = {};
    savedPrices.forEach((p) => { seed[p.instrument] = formatWithCommas(String(p.price)); });
    setEdits(seed);
  }, [open, savedPrices]);

  // ScenarioAnalysis → price columns only; else target Volume → volume columns;
  // else price columns. (Mirrors PricesCanvasColumns.reconcile.)
  const showPrice = stressType === StressTestType.ScenarioAnalysis || target !== StressTestTarget.Volume;
  const showVolume = stressType !== StressTestType.ScenarioAnalysis && target === StressTestTarget.Volume;

  const rows = (pv.data ?? []).filter((r) => r.symbol.toLowerCase().includes(search.trim().toLowerCase()));

  const collectModifiedPrices = (): StressTestModifiedPriceDto[] => {
    const out: StressTestModifiedPriceDto[] = [];
    Object.entries(edits).forEach(([sym, val]) => {
      const clean = stripCommas(val);
      if (clean !== '' && !Number.isNaN(Number(clean))) out.push({ instrument: sym, price: Number(clean) });
    });
    return out;
  };

  const save = () => {
    onSave(collectModifiedPrices());
    toast.success('Scenario saved successfully');
  };
  const clear = () => { setEdits({}); };

  if (!open) return null;

  return (
    <>
      <div className="offcanvas offcanvas-end w-50 show" tabIndex={-1} style={{ visibility: 'visible' }}>
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title">Instrument Prices and Volumes</h5>
          <button type="button" className="btn-close text-reset" aria-label="Close" onClick={() => setConfirmClose(true)} />
        </div>
        <div className="offcanvas-body">
          <div className="d-flex justify-content-end form-group mb-2">
            <button type="button" className="btn btn-primary" onClick={save}><i className="fa fa-save me-1" /> Save</button>
            <button type="button" className="btn btn-primary mx-1" onClick={clear}><i className="fa fa-edit me-1" /> Clear</button>
          </div>
          <input className="form-control mb-2" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div style={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
            <table className="table table-sm table-bordered mb-0">
              <thead>
                <tr>
                  <th>Instrument</th>
                  {showPrice && <th className="text-end">Price</th>}
                  {showPrice && <th className="text-end">New Price</th>}
                  {showVolume && <th className="text-end">Volume</th>}
                  {showVolume && <th className="text-end">New Volume</th>}
                </tr>
              </thead>
              <tbody>
                {pv.isLoading ? (
                  <tr><td colSpan={5} className="text-center py-3"><i className="fa fa-spinner fa-spin" /></td></tr>
                ) : (
                  rows.map((r: PricesAndVolumesDto) => (
                    <tr key={r.symbol}>
                      <td>{r.symbol}</td>
                      {showPrice && <td className="text-end">{money(r.bid, 5)}</td>}
                      {showPrice && (
                        <td className="text-end" style={{ width: 140 }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            className="form-control form-control-sm new-price-input"
                            value={edits[r.symbol] ?? ''}
                            onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                            onChange={(e) => { const v = cleanLive(e.target.value); setEdits((m) => ({ ...m, [r.symbol]: v })); }}
                          />
                        </td>
                      )}
                      {showVolume && <td className="text-end">{money(r.volume, 2)}</td>}
                      {showVolume && (
                        <td className="text-end" style={{ width: 140 }}>
                          <input type="number" step="any" className="form-control form-control-sm new-volume-input" disabled title="Volume scenarios are not applied" />
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ConfirmDialog
        show={confirmClose}
        title="Are you sure?"
        message="Do you really want to close this? Saved scenario will be cleared."
        confirmLabel="Yes, close it!"
        confirmVariant="primary"
        onConfirm={() => { setConfirmClose(false); onClose(); }}
        onCancel={() => setConfirmClose(false)}
      />
    </>
  );
}
